import "./MultiSelectDropdown.scss";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@me8eon/translation";
import { DropdownItem } from "./MultiSelectDropdown.types";
import { EonUiCheckbox, EonUiIcon } from "@eon-ui/eon-ui-components-react";

type Props = {
    options: DropdownItem[];
    selected: DropdownItem[];
    onChange: (next: DropdownItem[]) => void;
    /** Button label when nothing is selected, e.g. "All Categories" / "All Companies". */
    allLabel: string;
    /** Short form of `allLabel` shown in the menu's count row, e.g. "All categories". */
    allLabelShort: string;
    /** Suffix after the count when 2+ are selected, e.g. "categories selected". */
    selectedSuffix: string;
    /** aria-label for the toggle button, e.g. "Filter by category". */
    ariaLabel: string;
    /** Search input placeholder, e.g. "Search categories…". */
    searchPlaceholder: string;
    /** Prefix for the no-results message, e.g. "No categories match". */
    noMatchPrefix: string;
};

const keyOf = (c: DropdownItem) => c.value;

/** Generic searchable multi-select (categories, companies, ...). Empty selection = "All". Controlled by `selected`. */
const MultiSelectDropdown: React.FC<Props> = ({
    options,
    selected,
    onChange,
    allLabel,
    allLabelShort,
    selectedSuffix,
    ariaLabel,
    searchPlaceholder,
    noMatchPrefix,
}) => {
    const t = useTranslation();
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const ref = useRef<HTMLDivElement>(null);

    // Close on outside click.
    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (open && ref.current && !ref.current.contains(e.target as Node))
                setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    const selectedKeys = new Set(selected.map(keyOf));
    const toggle = (opt: DropdownItem) => {
        if (selectedKeys.has(keyOf(opt)))
            onChange(selected.filter((s) => keyOf(s) !== keyOf(opt)));
        else onChange([...selected, opt]);
    };

    const q = query.trim().toLowerCase();
    const filtered = q
        ? options.filter((o) => o.label.toLowerCase().includes(q))
        : options;

    const buttonLabel =
        selected.length === 0
            ? allLabel
            : selected.length === 1
              ? selected[0].label
              : `${selected.length} ${selectedSuffix}`;

    return (
        <div
            className={`multi_select_dropdown ${open ? "multi_select_dropdown--open" : ""}`}
            ref={ref}
        >
            <button
                type="button"
                className="multi_select_dropdown__control"
                onClick={() => setOpen((o) => !o)}
                aria-label={ariaLabel}
            >
                <EonUiIcon
                    name="search"
                    className="multi_select_dropdown__search-icon"
                    size="tiny"
                />
                <span className="multi_select_dropdown__value">
                    {buttonLabel}
                </span>
                {open ? (
                    <EonUiIcon
                        name="chevron_small_up"
                        className="multi_select_dropdown__chevron"
                        size="small"
                    />
                ) : (
                    <EonUiIcon
                        name="chevron_small_down"
                        className="multi_select_dropdown__chevron"
                        size="small"
                    />
                )}
            </button>
            {open && (
                <div className="multi_select_dropdown__menu">
                    <input
                        type="text"
                        className="multi_select_dropdown__search"
                        placeholder={searchPlaceholder}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        autoFocus
                    />
                    <div className="multi_select_dropdown__actions">
                        <span className="multi_select_dropdown__count">
                            {selected.length === 0
                                ? allLabelShort
                                : `${selected.length} ${tc("selected")}`}
                        </span>
                        {selected.length > 0 && (
                            <button
                                type="button"
                                className="po-link"
                                onClick={() => onChange([])}
                            >
                                {tc("clear")}
                            </button>
                        )}
                    </div>
                    <div className="multi_select_dropdown__list">
                        {filtered.map((opt) => {
                            const checked = selectedKeys.has(keyOf(opt));
                            return (
                                <label
                                    className="multi_select_dropdown__option"
                                    key={keyOf(opt)}
                                >
                                    {/* keyed on `checked` too: EonUiCheckbox doesn't reliably react to its
                                        `checked` prop changing when set externally (e.g. Clear all), so force
                                        a remount instead of relying on its internal state to stay in sync. */}
                                    <EonUiCheckbox
                                        key={`${keyOf(opt)}:${checked}`}
                                        checked={checked}
                                        onValueChanged={() => toggle(opt)}
                                        size="small"
                                        label={opt.label}
                                    />
                                </label>
                            );
                        })}
                        {filtered.length === 0 && (
                            <div className="multi_select_dropdown__empty">
                                {noMatchPrefix} “{query}”.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;
